import React from 'react';
import { UncontrolledPopover, PopoverHeader, PopoverBody, Badge } from 'reactstrap';
import { Icon } from '@iconify/react';
import doctor from '@iconify/icons-mdi/doctor';
import homeCity from '@iconify/icons-mdi/home-city';
import medicineBottle from '@iconify/icons-mdi/medicine-bottle';
import accountIcon from '@iconify/icons-mdi/account';
import pillIcon from '@iconify/icons-mdi/pill';
import calendarMonth from '@iconify/icons-mdi/calendar-month';
import commentText from '@iconify/icons-mdi/comment-text';
import chatIcon from '@iconify/icons-mdi/chat-processing-outline';
import noteTextOutline from '@iconify/icons-mdi/note-text-outline';
import hospitalBox from '@iconify/icons-mdi/hospital-box';
import noteMultipleOutline from '@iconify/icons-mdi/note-multiple-outline';
import {
    highCostAtom,
    highCostAtomFiltered,
    highCostAtomShownAlerts,
    highCostAlertsAtom,
    highCostAlertsAtomFiltered,
    highCostAlertsAtomShownAlerts,
    clarificationsAtom,
    clarificationsAtomFiltered,
    clarificationsAtomShownAlerts,
    priorAuthAtom,
    priorAuthAtomFiltered,
    priorAuthAtomShownAlerts,
    nonCoveredAtom,
    nonCoveredAtomFiltered,
    nonCoveredAtomShownAlerts,
    therapeuticIntAtom,
    therapeuticIntAtomFiltered,
    therapeuticIntAtomShownAlerts,
    admissionMedsAtom,
    admissionMedsAtomFiltered,
    admissionMedsAtomShownAlerts,
    highCostAtomUserSettings,
    highCostAlertsAtomUserSettings,
    clarificationsAtomUserSettings,
    priorAuthAtomUserSettings,
    nonCoveredAtomUserSettings,
    therapeuticIntAtomUserSettings,
    admissionMedsAtomUserSettings,
    refillTooSoonAtomShownAlerts,
    refillTooSoonAtomFiltered,
    refillTooSoonAtomUserSettings,
    refillTooSoonAtom,
    pumpPickupAtomUserSettings,
    pumpPickupAtom,
    pumpPickupAtomShownAlerts,
    pumpPickupAtomFiltered,
    narcoticsRefillAtomUserSettings,
    narcoticsRefillAtom,
    narcoticsRefillAtomShownAlerts,
    narcoticsRefillAtomFiltered,
    alfNewScriptAtomUserSettings,
    alfNewScriptAtom,
    alfNewScriptAtomShownAlerts,
    alfNewScriptAtomFiltered,
    alfCostResponseAtomUserSettings,
    alfCostResponseAtom,
    alfCostResponseAtomShownAlerts,
    alfCostResponseAtomFiltered,
    unavailableMedicationAlertsAtomUserSettings,
    unavailableMedicationAlertsAtom,
    unavailableMedicationAlertsAtomShownAlerts,
    unavailableMedicationAlertsAtomFiltered,
} from '../jotai-atoms/AlertAtoms';
import {
    archivedHighCostAtom,
    archivedHighCostAlertsAtom,
    archivedClarificationsAtom,
    archivedPriorAuthAtom,
    archivedNonCoveredAtom,
    archivedTherapeuticIntAtom,
    archivedAdmissionMedsAtom,
    archivedHighCostAtomFiltered,
    archivedHighCostAlertsAtomFiltered,
    archivedClarificationsAtomFiltered,
    archivedPriorAuthAtomFiltered,
    archivedNonCoveredAtomFiltered,
    archivedTherapeuticIntAtomFiltered,
    archivedAdmissionMedsAtomFiltered,
    archivedRefillTooSoonAtom,
    archivedRefillTooSoonAtomFiltered,
    archivedNarcoticsRefillAtom,
    archivedNarcoticsRefillAtomFiltered,
    archivedAlfNewScriptAtom,
    archivedAlfNewScriptAtomFiltered,
    archivedAlfCostResponseAtom,
    archivedAlfCostResponseAtomFiltered,
    archivedPumpPickupAtom,
    archivedPumpPickupAtomFiltered,
    archivedUnavailableMedicationAlertsAtom,
    archivedUnavailableMedicationAlertsAtomFiltered,
} from '../jotai-atoms/ArchivedAlertAtoms';
import { printPumpPickupList, printNarcoticsRefillList, printNarcoticsRefillTicket, printAlfWidgetList, printAlfWidgetTicket } from '../utility-services/reportGenerator';
import { getDate, getDateAndTime, getDateAndTimeStamp } from '../utility-components/dateUtils';
import { getAlertCommentsForDisplay } from '../custom-components/TableRowDetailSharedUtils';

// this is careted as a function since the column format field expects a function
const getArrayLengthWithBoldFont = (arr) => {
    return (
        <div className="w-100 text-center">
            <strong>{arr.length}</strong>
        </div>
    );
};

function CommentCell(cell, id) {
    if (!cell) return '';

    return (
        <div id={`comment-popover-${id}`} className="d-flex justify-content-center" style={{ width: 'max-content' }}>
            <Icon icon={noteTextOutline} height="25" width="25" className="comment-icon" />
            <UncontrolledPopover placement="top" target={`comment-popover-${id}`} trigger="hover">
                {/* <PopoverHeader>Title goes here</PopoverHeader> */}
                <PopoverBody>{cell}</PopoverBody>
            </UncontrolledPopover>
        </div>
    );
}

function MultiCommentCell(cell, id, row) {
    const thread = row ? getAlertCommentsForDisplay(row) : [];
    if (!thread.length) return '';

    return (
        <div id={`comment-popover-${id}`} className="d-flex justify-content-center" style={{ width: 'max-content' }}>
            <Icon icon={noteMultipleOutline} height="25" width="25" className="comment-icon" />
            <UncontrolledPopover
                placement="top"
                target={`comment-popover-${id}`}
                trigger="hover"
                style={{ maxHeight: '200px', overflow: 'auto' }}>
                {thread.map((comment, i) => (
                    <React.Fragment key={i}>
                        {i > 0 && i !== thread.length && <hr style={{ borderTop: '2px solid #eef2f7' }} />}
                        <PopoverBody>
                            {comment.reason ? (
                                <div className="mb-1">
                                    <b>{comment.reason}</b>
                                </div>
                            ) : null}
                            <small>
                                <i>{getDateAndTime(comment.loggedAt)}</i>
                            </small>
                            <br />
                            {comment.comment}
                        </PopoverBody>
                    </React.Fragment>
                ))}
            </UncontrolledPopover>
        </div>
    );
}

function ReasonAndCommentCell(cell, id, row) {
    const thread = row ? getAlertCommentsForDisplay(row) : [];
    if (!thread.length) return '';

    return (
        <div id={`comment-popover-2-${id}`} className="d-flex justify-content-center" style={{ width: 'max-content' }}>
            <Icon icon={noteMultipleOutline} height="25" width="25" className="comment-icon" />
            <UncontrolledPopover
                placement="top"
                target={`comment-popover-2-${id}`}
                trigger="hover"
                style={{ maxHeight: '200px', overflow: 'auto' }}>
                {thread.map((comment, i) => (
                    <React.Fragment key={i}>
                        <PopoverHeader>
                            {comment.reason ? (
                                <div className="mb-1">
                                    <b>{comment.reason}</b>
                                </div>
                            ) : (
                                <div className="mb-1" />
                            )}
                            <div>
                                <small>
                                    <i>{getDateAndTime(comment.loggedAt)}</i>
                                </small>
                            </div>
                        </PopoverHeader>
                        <PopoverBody>{comment.comment}</PopoverBody>
                    </React.Fragment>
                ))}
            </UncontrolledPopover>
        </div>
    );
}

function capitalizeFirstLetter(word) {
    return word[0].toUpperCase() + word.substring(1);
}

const PriorAuthStatusBadge = (data) => {
    return (
        <Badge
            pill
            // color={data.toLowerCase() === 'pending' ? 'danger' : 'success'}
            className={`badge badge-outline-${data.toLowerCase() === 'pending' ? 'danger' : 'success'}`}>
            {capitalizeFirstLetter(data.toLowerCase())}
        </Badge>
    );
};

const ActionStatusBadge = (data) => {
    const getUserActionBadgeColor = (action = '') => {
        if (!action) return;
        if (action.toLowerCase() === 'approve') return 'success';
        if (action.toLowerCase() === 'deny') return 'danger';
        if (action.toLowerCase() === 'approveonce') return 'secondary';
        if (action.toLowerCase() === 'approve_once') return 'secondary';
        if (action.toLowerCase() === 'ask') return 'warning';
        else return 'primary';
    };

    const getUserActionLabel = (action = '') => {
        if (!action) return;
        if (action.toLowerCase() === 'approve') return 'Approved';
        if (action.toLowerCase() === 'deny') return 'Denied';
        if (action.toLowerCase() === 'approveonce') return 'Approved Once';
        if (action.toLowerCase() === 'approve_once') return 'Approved Once';
        else return action;
    };

    return (
        <Badge
            pill
            // color={data.toLowerCase() === 'pending' ? 'danger' : 'success'}
            className={'badge badge-outline-' + getUserActionBadgeColor(data)}>
            {getUserActionLabel(data)}
        </Badge>
    );
};
const StatusBadgeWithAutoApproval = (data, rxGuid, row) => {
    const isAutoApproved = Array.isArray(row?.requestedActions)
        ? row.requestedActions.some((action) => action?.autoResponded === true)
        : false;

    if (!isAutoApproved) {
        return ActionStatusBadge(data);
    }

    return (
        <span title={data}>
            <Badge pill className="badge badge-outline-primary">
                Auto Approved
            </Badge>
        </span>
    );
};

const MedStatusBadge = (status) => {
    if (!status) return '';

    const isDenied = String(status).toLowerCase() === 'denied';
    const displayStatus =
        String(status).charAt(0).toUpperCase() + String(status).slice(1).toLowerCase();

    return (
        <Badge
            pill
            style={{
                backgroundColor: isDenied ? '#ffe2e2' : '#fef3c6',
                color: isDenied ? '#cd5a5c' : '#d26a00',
                padding: '6px 10px',
                alignItems: 'center',
            }}>
            {displayStatus}
        </Badge>
    );
};

const UnavailableMedStatusBadge = (status, _rxGuid, alert) => {
    const resolvedStatus =
        status ?? alert?.AvailabilityStatus ?? alert?.status ?? alert?.Status ?? alert?.medStatus ?? alert?.MedStatus;

    if (!resolvedStatus) return '';

    const isBackorder = String(resolvedStatus).toUpperCase() === 'BACKORDER';

    return (
        <Badge
            pill
            style={{
                backgroundColor: isBackorder ? '#ffe2e2' : '#fef9c2',
                color: isBackorder ? '#c10007' : '#a65f00',
                fontWeight: 600,
                padding: '6px 10px',
                alignItems: 'center',
                textTransform: 'uppercase',
            }}>
            {resolvedStatus}
        </Badge>
    );
};

const ChatColumn = (data) => {
    // console.log(data);
    const messages = data?.filter((i) => {
        return i.action === 'Reply';
    });
    // console.log(messages);
    let unRead = messages?.some((i) => i.readByFaci !== true);

    return (
        <span style={{ fontSize: '20px' }} hidden={!data?.length}>
            {' '}
            <Icon icon={chatIcon} style={{ color: unRead ? '#fd7e14' : "" }} />
        </span>
    );
};
const patientNameCol = (data) => {
    return (
        <div className="col-truncate" title={data}>
            {data.toLowerCase()}
        </div>
    );
};

const faciNameCol = (data, guid, row) => {
    return <div title={row.facID}>{data}</div>;
};

const prescriberCol = (data, guid, row) => {
    return row?.phName || row?.phname || row?.prescriberName || data || '';
};

const pumpRequestedDateCol = (data, guid, row) => {
    if (row?.pickupRequests > 0 && row?.lastPickupRequest != null) {
        return getDate(data || row?.lastPickupRequest);
    }
    if (row?.billedFinalPump) {
        return <span style={{ color: '#452f69', fontWeight: 600 }}>Billed: {getDate(row.alertUpdatedOn)}</span>;
    }
    if (row?.returnDt) {
        return <span style={{ color: '#28a745', fontWeight: 600 }}>Returned: {getDate(row.returnDt)}</span>;
    }
    if (row?.returnDueDt) {
        return <span style={{ color: '#dc3545', fontWeight: 600 }}>Due: {getDate(row.returnDueDt)}</span>;
    }
    return getDate(data);
};

function formatCost(cost) {
    return <div>{cost && <> ${cost?.toLocaleString('en-US')}</>}</div>;
}

// Single source for widget config and grid column definitions. Used by SingleAlertWidgetV2 and routes.
export const WidgetsData = [
    {
        name: 'authorization',
        stateName: 'highCostList',
        sidebarName: 'Authorization',
        path: 'highCost',
        category: 'highCost',
        requestActionPermissionWidgetName: 'highCost',
        defaultSortField: 'alertCreatedOn',
        atom: highCostAtom,
        atomShownAlerts: highCostAtomShownAlerts,
        atomFilteredAlerts: highCostAtomFiltered,
        sortOrder: highCostAtomUserSettings.sortOrder,
        active: true,
        hidden: highCostAtomUserSettings.hidden,
        position: 0,
        archived: {
            atom: archivedHighCostAtom,
            atomFilteredAlerts: archivedHighCostAtomFiltered,
        },
        refreshSchedule: {
            mins: 0,
            scnds: 0,
        },
        isV2Alert: true,
        usesCursorPaging: false,
        columns: [
            {
                name: 'Patient',
                key: 'patName',
                sort: true,
                icon: accountIcon,
                prefix: '',
                suffix: '',
                format: patientNameCol,
            },
            {
                name: 'Facility',
                key: 'facID',
                sort: true,
                icon: homeCity,
                prefix: '',
                suffix: '',
                format: faciNameCol,
                className: 'faci-col',
            },
            {
                name: 'Drug',
                key: 'drugLabelName',
                sort: true,
                icon: pillIcon,
                prefix: '',
                suffix: '',
                format: '',
            },
            {
                name: 'Cost',
                key: 'ttlPrice',
                sort: true,
                icon: null,
                prefix: '',
                suffix: '',
                format: formatCost,
            },
            {
                name: null,
                key: 'requestedActions',
                sort: false,
                icon: null,
                prefix: '',
                suffix: '',
                format: ChatColumn,
            },
            {
                name: 'Pharm Stat',
                key: 'pharmacyStatus',
                sort: false,
                icon: null,
                prefix: '',
                suffix: '',
                format: ActionStatusBadge,
            },
            {
                name: 'Status',
                key: 'srxStatus',
                sort: false,
                icon: null,
                prefix: '',
                suffix: '',
                format: StatusBadgeWithAutoApproval,
            },
            // {
            //     name: 'Modified',
            //     key: 'lastModified',
            //     sort: false,
            //     icon: calendarMonth,
            //     prefix: '',
            //     suffix: '',
            //     format: getDate
            // },
            {
                name: 'Alerted On',
                key: 'alertCreatedOn',
                sort: true,
                icon: calendarMonth,
                prefix: '',
                suffix: '',
                format: getDate,
            },
        ],
    },
    {
        name: 'high cost alert',
        stateName: 'highCostAlertsListFlat',
        sidebarName: 'High Cost Alert',
        path: 'highcostalerts',
        category: 'highcostalerts',
        requestActionPermissionWidgetName: 'highcostalerts',
        defaultSortField: 'alertCreatedOn',
        atom: highCostAlertsAtom,
        atomShownAlerts: highCostAlertsAtomShownAlerts,
        atomFilteredAlerts: highCostAlertsAtomFiltered,
        sortOrder: highCostAlertsAtomUserSettings.sortOrder,
        active: true,
        hidden: highCostAlertsAtomUserSettings.hidden,
        position: 0,
        archived: {
            atom: archivedHighCostAlertsAtom,
            atomFilteredAlerts: archivedHighCostAlertsAtomFiltered,
        },
        refreshSchedule: {
            mins: 0,
            scnds: 0,
        },
        isV2Alert: true,
        usesCursorPaging: false,
        columns: [
            {
                name: 'Patient',
                key: 'patName',
                sort: true,
                icon: accountIcon,
                prefix: '',
                suffix: '',
                format: patientNameCol,
            },
            {
                name: 'Facility',
                key: 'facID',
                sort: true,
                icon: homeCity,
                prefix: '',
                suffix: '',
                format: faciNameCol,
                className: 'faci-col',
            },
            {
                name: 'Station',
                key: 'nsID',
                sort: true,
                icon: doctor,
                prefix: '',
                suffix: '',
                format: '',
                className: 'faci-col',
            },
            {
                name: 'Drug',
                key: 'drugLabelName',
                sort: true,
                icon: pillIcon,
                prefix: '',
                suffix: '',
                format: '',
            },
            {
                name: 'Cost',
                key: 'ttlPrice',
                sort: true,
                icon: null,
                prefix: '',
                suffix: '',
                format: formatCost,
            },
            {
                name: null,
                key: 'requestedActions',
                sort: false,
                icon: null,
                prefix: '',
                suffix: '',
                format: ChatColumn,
            },
            // {
            //     name: 'Pharm Stat',
            //     key: 'pharmacyStatus',
            //     sort: false,
            //     icon: null,
            //     prefix: '',
            //     suffix: '',
            //     format: ActionStatusBadge,
            // },
            {
                name: 'Status',
                key: 'srxStatus',
                sort: false,
                icon: null,
                prefix: '',
                suffix: '',
                format: ActionStatusBadge,
            },
            // {
            //     name: 'Modified',
            //     key: 'lastModified',
            //     sort: false,
            //     icon: calendarMonth,
            //     prefix: '',
            //     suffix: '',
            //     format: getDate
            // },
            {
                name: 'Alerted On',
                key: 'alertCreatedOn',
                sort: true,
                icon: calendarMonth,
                prefix: '',
                suffix: '',
                format: getDate,
            },
        ],
    },
    {
        name: 'clarifications',
        stateName: 'openBatchList',
        path: 'openbatch',
        category: 'clarifications',
        requestActionPermissionWidgetName: 'openBatch',
        atom: clarificationsAtom,
        atomShownAlerts: clarificationsAtomShownAlerts,
        atomFilteredAlerts: clarificationsAtomFiltered,
        position: 1,
        archived: {
            atom: archivedClarificationsAtom,
            atomFilteredAlerts: archivedClarificationsAtomFiltered,
        },
        defaultSortField: 'alertCreatedOn',
        sidebarName: 'Clarifications',
        sortOrder: clarificationsAtomUserSettings.sortOrder,
        active: true,
        hidden: clarificationsAtomUserSettings.hidden,
        refreshSchedule: {
            mins: 6,
            scnds: 40,
        },
        isV2Alert: true,
        usesCursorPaging: false,
        columns: [
            {
                name: 'Patient',
                key: 'patName',
                sort: true,
                icon: accountIcon,
                prefix: '',
                suffix: '',
                format: patientNameCol,
            },
            {
                name: 'Facility',
                key: 'facID',
                sort: true,
                icon: homeCity,
                prefix: '',
                suffix: '',
                format: faciNameCol,
                className: 'faci-col',
            },
            {
                name: 'Station',
                key: 'nsID',
                sort: true,
                icon: doctor,
                prefix: '',
                suffix: '',
                format: '',
                className: 'faci-col',
            },
            {
                name: 'Drug',
                key: 'drugLabelName',
                sort: true,
                icon: pillIcon,
                prefix: '',
                suffix: '',
                format: '',
            },
            {
                name: null,
                key: 'requestedActions',
                sort: false,
                icon: null,
                prefix: '',
                suffix: '',
                format: ChatColumn,
            },
            {
                name: 'Sig',
                key: 'sig',
                sort: false,
                icon: null,
                prefix: '',
                suffix: '',
                format: CommentCell,
            },
            {
                name: 'Comments',
                key: 'issues',
                sort: false,
                icon: commentText,
                prefix: '',
                suffix: '',
                format: ReasonAndCommentCell,
                style: {
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                },
            },
            {
                name: 'Alerted On',
                key: 'alertCreatedOn',
                sort: true,
                icon: calendarMonth,
                prefix: '',
                suffix: '',
                format: getDate,
            },
        ],
    },
    {
        name: 'non-covered',
        stateName: 'nonCoveredList',
        category: 'nonCovered',
        requestActionPermissionWidgetName: 'nonCovered',
        atom: nonCoveredAtom,
        atomShownAlerts: nonCoveredAtomShownAlerts,
        atomFilteredAlerts: nonCoveredAtomFiltered,
        archived: {
            atom: archivedNonCoveredAtom,
            atomFilteredAlerts: archivedNonCoveredAtomFiltered,
        },
        sidebarName: 'Non-Covered',
        path: 'nonCovered',
        active: true,
        hidden: nonCoveredAtomUserSettings.hidden,
        sortOrder: nonCoveredAtomUserSettings.sortOrder,
        position: 3,
        defaultSortField: 'alertCreatedOn',
        refreshSchedule: {
            mins: 1,
            scnds: 20,
        },
        isV2Alert: true,
        usesCursorPaging: false,
        columns: [
            {
                name: 'Patient',
                key: 'patName',
                sort: true,
                icon: accountIcon,
                prefix: '',
                suffix: '',
                format: patientNameCol,
            },
            {
                name: 'Facility',
                key: 'facID',
                sort: true,
                icon: homeCity,
                prefix: '',
                suffix: '',
                format: faciNameCol,
                className: 'faci-col',
            },
            {
                name: 'Station',
                key: 'nsID',
                sort: true,
                icon: doctor,
                prefix: '',
                suffix: '',
                format: '',
                className: 'faci-col',
            },
            {
                name: 'Drug',
                key: 'drugLabelName',
                sort: true,
                icon: pillIcon,
                prefix: '',
                suffix: '',
                format: '',
            },
            // {
            //     name: 'Cost',
            //     key: 'ttlPrice',
            //     sort: true,
            //     icon: null,
            //     prefix: '',
            //     suffix: '',
            //     format: formatCost,
            // },
            {
                name: null,
                key: 'requestedActions',
                sort: false,
                icon: null,
                prefix: '',
                suffix: '',
                format: ChatColumn,
            },
            {
                name: 'Comments',
                key: 'issues',
                sort: false,
                icon: commentText,
                prefix: '',
                suffix: '',
                format: MultiCommentCell,
                // style: {
                //     whiteSpace: 'nowrap',
                //     textOverflow: 'ellipsis',
                //     overflow: 'hidden'
                // }
            },
            {
                name: 'Alerted On',
                key: 'alertCreatedOn',
                sort: true,
                icon: calendarMonth,
                prefix: '',
                suffix: '',
                format: getDate,
            },
        ],
    },
    {
        name: 'prior authorization medication',
        category: 'priorAuth',
        requestActionPermissionWidgetName: 'priorAuth',
        stateName: 'priorAuthList',
        atom: priorAuthAtom,
        atomShownAlerts: priorAuthAtomShownAlerts,
        atomFilteredAlerts: priorAuthAtomFiltered,
        archived: {
            atom: archivedPriorAuthAtom,
            atomFilteredAlerts: archivedPriorAuthAtomFiltered,
        },
        sidebarName: 'Prior Auth',
        path: 'priorAuth',
        active: true,
        hidden: priorAuthAtomUserSettings.hidden,
        sortOrder: priorAuthAtomUserSettings.sortOrder,
        position: 2,
        defaultSortField: 'alertCreatedOn',
        refreshSchedule: {
            mins: 2,
            scnds: 40,
        },
        isV2Alert: true,
        usesCursorPaging: false,
        columns: [
            {
                name: 'Patient',
                key: 'patName',
                sort: true,
                icon: accountIcon,
                prefix: '',
                suffix: '',
                format: patientNameCol,
            },
            {
                name: 'Facility',
                key: 'facID',
                sort: true,
                icon: homeCity,
                prefix: '',
                suffix: '',
                format: faciNameCol,
                className: 'faci-col',
            },
            {
                name: 'Station',
                key: 'nsID',
                sort: true,
                icon: doctor,
                prefix: '',
                suffix: '',
                format: '',
                className: 'faci-col',
            },
            {
                name: 'Drug',
                key: 'drugLabelName',
                sort: true,
                icon: pillIcon,
                prefix: '',
                suffix: '',
                format: '',
            },
            {
                name: 'Cost',
                key: 'ttlPrice',
                sort: true,
                icon: null,
                prefix: '',
                suffix: '',
                format: formatCost,
            },
            {
                name: null,
                key: 'requestedActions',
                sort: false,
                icon: null,
                prefix: '',
                suffix: '',
                format: ChatColumn,
            },
            // {
            //     name: 'Billing Comment',
            //     key: 'billingComment',
            //     sort: false,
            //     icon: commentText,
            //     prefix: '',
            //     suffix: '',
            //     format: CommentCell,
            //     style: {
            //         whiteSpace: 'nowrap',
            //         textOverflow: 'ellipsis',
            //         overflow: 'hidden'
            //     }
            // },
            {
                name: 'Status',
                key: 'srxStatus',
                sort: false,
                icon: null,
                prefix: '',
                suffix: '',
                format: PriorAuthStatusBadge,
            },
            {
                name: 'Alerted On',
                key: 'alertCreatedOn',
                sort: true,
                icon: calendarMonth,
                prefix: '',
                suffix: '',
                format: getDate,
            },
        ],
    },
    {
        name: 'therapeutic interchanged',
        stateName: 'therapeuticInterchangeList',
        category: 'therapeuticInt',
        requestActionPermissionWidgetName: 'theraInt',
        path: 'therapeuticint',
        atom: therapeuticIntAtom,
        atomShownAlerts: therapeuticIntAtomShownAlerts,
        atomFilteredAlerts: therapeuticIntAtomFiltered,
        archived: {
            atom: archivedTherapeuticIntAtom,
            atomFilteredAlerts: archivedTherapeuticIntAtomFiltered,
        },
        sidebarName: 'Therapeutic Int',
        active: true,
        hidden: therapeuticIntAtomUserSettings.hidden,
        defaultSortField: 'alertCreatedOn',
        sortOrder: therapeuticIntAtomUserSettings.sortOrder,
        position: 4,
        refreshSchedule: {
            mins: 4,
            scnds: 0,
        },
        isV2Alert: true,
        usesCursorPaging: false,
        columns: [
            {
                name: 'Patient',
                key: 'patName',
                sort: true,
                icon: accountIcon,
                prefix: '',
                suffix: '',
                format: patientNameCol,
            },
            {
                name: 'Facility',
                key: 'facID',
                sort: true,
                icon: homeCity,
                prefix: '',
                suffix: '',
                format: faciNameCol,
                className: 'faci-col',
            },
            {
                name: 'Station',
                key: 'nsID',
                sort: true,
                icon: doctor,
                prefix: '',
                suffix: '',
                format: '',
                className: 'faci-col',
            },
            {
                name: 'Prescribed Drug',
                key: 'prescribedDrugName',
                sort: true,
                icon: pillIcon,
                prefix: '',
                suffix: '',
                format: '',
            },
            {
                name: 'Drug Dispensed',
                key: 'drugLabelName',
                sort: true,
                icon: pillIcon,
                prefix: '',
                suffix: '',
                format: '',
            },
            {
                name: null,
                key: 'requestedActions',
                sort: false,
                icon: null,
                prefix: '',
                suffix: '',
                format: ChatColumn,
            },
            {
                name: 'Alerted On',
                key: 'alertCreatedOn',
                sort: true,
                icon: calendarMonth,
                prefix: '',
                suffix: '',
                format: getDate,
            },
        ],
    },
    {
        name: 'admission / medication review',
        stateName: 'admissionMedsList',
        category: 'admissionMeds',
        requestActionPermissionWidgetName: 'admissionMeds',
        atom: admissionMedsAtom,
        atomShownAlerts: admissionMedsAtomShownAlerts,
        atomFilteredAlerts: admissionMedsAtomFiltered,
        archived: {
            atom: archivedAdmissionMedsAtom,
            atomFilteredAlerts: archivedAdmissionMedsAtomFiltered,
        },
        path: 'admissionmeds',
        sidebarName: 'Admission Meds',
        active: true,
        hidden: admissionMedsAtomUserSettings.hidden,
        defaultSortField: 'admDate',
        sortOrder: admissionMedsAtomUserSettings.sortOrder,
        position: 5,
        refreshSchedule: {
            mins: 5,
            scnds: 20,
        },
        isV2Alert: true,
        usesCursorPaging: false,
        columns: [
            {
                name: 'Patient',
                key: 'patName',
                sort: true,
                icon: accountIcon,
                prefix: '',
                suffix: '',
                format: patientNameCol,
            },
            {
                name: 'Facility',
                key: 'facID',
                sort: true,
                icon: homeCity,
                prefix: '',
                suffix: '',
                format: faciNameCol,
                className: 'faci-col',
            },
            {
                name: 'Station',
                key: 'nsID',
                sort: true,
                icon: doctor,
                prefix: '',
                suffix: '',
                format: '',
                className: 'faci-col',
            },
            {
                name: 'Admission Meds',
                key: 'admissionMedsDetailsList',
                sort: false,
                icon: hospitalBox,
                prefix: '',
                suffix: '',
                format: getArrayLengthWithBoldFont,
            },
            {
                name: null,
                key: 'requestedActions',
                sort: false,
                icon: null,
                prefix: '',
                suffix: '',
                format: ChatColumn,
            },
            {
                name: 'Admission Date',
                key: 'admDate',
                sort: true,
                icon: calendarMonth,
                prefix: '',
                suffix: '',
                format: getDate,
            },
        ],
    },
    {
        name: 'refill too soon',
        stateName: 'refillTooSoonList',
        category: 'refillTooSoon',
        path: 'refilltoosoon',
        requestActionPermissionWidgetName: 'refillTooSoon',
        defaultSortField: 'alertCreatedOn',
        sidebarName: 'Refill Too Soon',
        atom: refillTooSoonAtom,
        atomShownAlerts: refillTooSoonAtomShownAlerts,
        atomFilteredAlerts: refillTooSoonAtomFiltered,
        sortOrder: refillTooSoonAtomUserSettings.sortOrder,
        active: true,
        hidden: refillTooSoonAtomUserSettings.hidden,
        position: 0,
        archived: {
            atom: archivedRefillTooSoonAtom,
            atomFilteredAlerts: archivedRefillTooSoonAtomFiltered,
        },
        refreshSchedule: {
            mins: 0,
            scnds: 0,
        },
        isV2Alert: true,
        usesCursorPaging: false,
        columns: [
            {
                name: 'Patient',
                key: 'patName',
                sort: true,
                icon: accountIcon,
                prefix: '',
                suffix: '',
                format: patientNameCol,
            },
            {
                name: 'Facility',
                key: 'facID',
                sort: true,
                icon: homeCity,
                prefix: '',
                suffix: '',
                format: faciNameCol,
                className: 'faci-col',
            },
            {
                name: 'Station',
                key: 'nsID',
                sort: true,
                icon: doctor,
                prefix: '',
                suffix: '',
                format: '',
                className: 'faci-col',
            },
            {
                name: 'Drug',
                key: 'drugLabelName',
                sort: true,
                icon: medicineBottle,
                prefix: '',
                suffix: '',
                format: '',
                className: 'faci-col',
            },
            {
                name: null,
                key: 'requestedActions',
                sort: false,
                icon: null,
                prefix: '',
                suffix: '',
                format: ChatColumn,
            },
            {
                name: 'Due Date',
                key: 'dueDate',
                sort: true,
                icon: calendarMonth,
                prefix: '',
                suffix: '',
                format: getDate,
                className: 'faci-col',
            },
            {
                name: 'Alerted On',
                key: 'alertCreatedOn',
                sort: true,
                icon: calendarMonth,
                prefix: '',
                suffix: '',
                format: getDate,
                className: 'faci-col',
            },
        ]
    },
    {
        name: 'pump-pickup',
        stateName: 'pumpPickupList',
        category: 'pumpPickup',
        path: 'pumppickup',
        requestActionPermissionWidgetName: 'pumpPickup',
        defaultSortField: 'alertCreatedOn',
        sidebarName: 'Pump Pickup',
        atom: pumpPickupAtom,
        atomShownAlerts: pumpPickupAtomShownAlerts,
        atomFilteredAlerts: pumpPickupAtomFiltered,
        sortOrder: pumpPickupAtomUserSettings.sortOrder,
        active: true,
        hidden: pumpPickupAtomUserSettings.hidden,
        position: 0,
        archived: {
            atom: archivedPumpPickupAtom,
            atomFilteredAlerts: archivedPumpPickupAtomFiltered,
        },
        refreshSchedule: {
            mins: 0,
            scnds: 0,
        },
        isV2Alert: true,
        usesCursorPaging: false,
        listPrint: true,
        listPrintFunction: printPumpPickupList,
        columns: [
            {
                name: 'Patient',
                key: 'patName',
                sort: true,
                icon: accountIcon,
                prefix: '',
                suffix: '',
                format: '',
            },
            {
                name: 'Facility',
                key: 'facID',
                sort: true,
                icon: homeCity,
                prefix: '',
                suffix: '',
                format: faciNameCol,
                className: 'faci-col',
            },
            {
                name: 'Station',
                key: 'nsID',
                sort: true,
                icon: doctor,
                prefix: '',
                suffix: '',
                format: '',
                className: 'faci-col',
            },
            {
                name: 'Serial Number',
                key: 'pumpSerialNo',
                sort: true,
                icon: medicineBottle,
                prefix: '',
                suffix: '',
                format: '',
                className: 'faci-col',
            },
            {
                name: 'Requested Date',
                key: 'lastPickupRequest',
                sort: true,
                icon: calendarMonth,
                prefix: '',
                suffix: '',
                format: pumpRequestedDateCol,
                className: 'faci-col',
            },
            {
                name: null,
                key: 'requestedActions',
                sort: false,
                icon: null,
                prefix: '',
                suffix: '',
                format: ChatColumn,
            },
        ]
    },
    {
        name: 'NARCOTICS - NEW SCRIPT NEEDED',
        stateName: 'narcoticsRefillList',
        category: 'narcoticsRefill',
        path: 'narcoticsrefill',
        requestActionPermissionWidgetName: 'narcoticsRefill',
        defaultSortField: 'alertCreatedOn',
        sidebarName: 'Narcotics Refill',
        atom: narcoticsRefillAtom,
        atomShownAlerts: narcoticsRefillAtomShownAlerts,
        atomFilteredAlerts: narcoticsRefillAtomFiltered,
        sortOrder: narcoticsRefillAtomUserSettings.sortOrder,
        active: true,
        hidden: narcoticsRefillAtomUserSettings.hidden,
        position: 0,
        archived: {
            atom: archivedNarcoticsRefillAtom,
            atomFilteredAlerts: archivedNarcoticsRefillAtomFiltered,
        },
        refreshSchedule: {
            mins: 0,
            scnds: 0,
        },
        isV2Alert: true,
        usesCursorPaging: false,
        listPrint: true,
        listPrintFunction: printNarcoticsRefillList,
        ticketPrint: true,
        ticketPrintFunction: printNarcoticsRefillTicket,
        columns: [
            {
                name: 'Patient',
                key: 'patName',
                sort: true,
                icon: accountIcon,
                prefix: '',
                suffix: '',
                format: patientNameCol,
                className: 'faci-col',
            },
            {
                name: 'Facility',
                key: 'facID',
                sort: true,
                icon: homeCity,
                prefix: '',
                suffix: '',
                format: faciNameCol,
                className: 'faci-col',
            },
            {
                name: 'Station',
                key: 'nsID',
                sort: true,
                icon: doctor,
                prefix: '',
                suffix: '',
                format: '',
                className: 'faci-col',
            },
            {
                name: 'Drug Name',
                key: 'drugLabelName',
                sort: true,
                icon: medicineBottle,
                prefix: '',
                suffix: '',
                format: '',
                className: 'faci-col',
            },
            {
                name: 'Prescriber',
                key: 'phName',
                sort: true,
                icon: doctor,
                prefix: '',
                suffix: '',
                format: prescriberCol,
                className: 'faci-col',
            },
            {
                name: null,
                key: 'requestedActions',
                sort: false,
                icon: null,
                prefix: '',
                suffix: '',
                format: ChatColumn,
            },
            {
                name: 'Alerted On',
                key: 'alertCreatedOn',
                sort: true,
                icon: calendarMonth,
                prefix: '',
                suffix: '',
                format: getDate,
                className: 'faci-col',
            },
        ]
    },
    {
        name: 'ALF - NEW SCRIPT NEEDED',
        stateName: 'alfNewScriptList',
        category: 'alfNewScript',
        path: 'alfnewscriptneeded',
        requestActionPermissionWidgetName: 'alfNewScript',
        defaultSortField: 'alertCreatedOn',
        sidebarName: 'ALF New Script',
        atom: alfNewScriptAtom,
        atomShownAlerts: alfNewScriptAtomShownAlerts,
        atomFilteredAlerts: alfNewScriptAtomFiltered,
        sortOrder: alfNewScriptAtomUserSettings.sortOrder,
        active: true,
        hidden: alfNewScriptAtomUserSettings.hidden,
        position: 0,
        archived: {
            atom: archivedAlfNewScriptAtom,
            atomFilteredAlerts: archivedAlfNewScriptAtomFiltered,
        },
        refreshSchedule: {
            mins: 0,
            scnds: 0,
        },
        isV2Alert: true,
        usesCursorPaging: false,
        listPrint: true,
        listPrintFunction: printAlfWidgetList,
        ticketPrint: true,
        ticketPrintFunction: printAlfWidgetTicket,
        columns: [
            {
                name: 'Patient',
                key: 'patName',
                sort: true,
                icon: accountIcon,
                prefix: '',
                suffix: '',
                format: patientNameCol,
                className: 'faci-col',
            },
            {
                name: 'Facility',
                key: 'facID',
                sort: true,
                icon: homeCity,
                prefix: '',
                suffix: '',
                format: faciNameCol,
                className: 'faci-col',
            },
            {
                name: 'Station',
                key: 'nsID',
                sort: true,
                icon: doctor,
                prefix: '',
                suffix: '',
                format: '',
                className: 'faci-col',
            },
            {
                name: 'Drug Name',
                key: 'drugLabelName',
                sort: true,
                icon: medicineBottle,
                prefix: '',
                suffix: '',
                format: '',
                className: 'faci-col',
            },
            {
                name: 'Prescriber',
                key: 'prescribingDoctor',
                sort: true,
                icon: doctor,
                prefix: '',
                suffix: '',
                format: '',
                className: 'faci-col',
            },

            {
                name: null,
                key: 'requestedActions',
                sort: false,
                icon: null,
                prefix: '',
                suffix: '',
                format: ChatColumn,
            },
            {
                name: 'Alerted On',
                key: 'alertCreatedOn',
                sort: true,
                icon: calendarMonth,
                prefix: '',
                suffix: '',
                format: getDate,
                className: 'faci-col',
            },
        ],
    },
    {
        name: 'ALF - HIGH COST DENIALS',
        stateName: 'alfCostResponseList',
        category: 'alfCostResponse',
        path: 'alfnonactionordenial',
        requestActionPermissionWidgetName: 'alfCostResponse',
        defaultSortField: 'alertCreatedOn',
        sidebarName: 'ALF High Cost Alerts',
        atom: alfCostResponseAtom,
        atomShownAlerts: alfCostResponseAtomShownAlerts,
        atomFilteredAlerts: alfCostResponseAtomFiltered,
        sortOrder: alfCostResponseAtomUserSettings.sortOrder,
        active: true,
        hidden: alfCostResponseAtomUserSettings.hidden,
        position: 0,
        archived: {
            atom: archivedAlfCostResponseAtom,
            atomFilteredAlerts: archivedAlfCostResponseAtomFiltered,
        },
        refreshSchedule: {
            mins: 0,
            scnds: 0,
        },
        isV2Alert: true,
        usesCursorPaging: false,
        listPrint: true,
        listPrintFunction: printAlfWidgetList,
        ticketPrint: true,
        ticketPrintFunction: printAlfWidgetTicket,
        columns: [
            {
                name: 'Patient',
                key: 'patName',
                sort: true,
                icon: accountIcon,
                prefix: '',
                suffix: '',
                format: patientNameCol,
                className: 'faci-col',
            },
            {
                name: 'Facility',
                key: 'facID',
                sort: true,
                icon: homeCity,
                prefix: '',
                suffix: '',
                format: faciNameCol,
                className: 'faci-col',
            },
            {
                name: 'Station',
                key: 'nsID',
                sort: true,
                icon: doctor,
                prefix: '',
                suffix: '',
                format: '',
                className: 'faci-col',
            },
            {
                name: 'Drug Name',
                key: 'drugLabelName',
                sort: true,
                icon: medicineBottle,
                prefix: '',
                suffix: '',
                format: '',
                className: 'faci-col',
            },
            {
                name: 'Med Status',
                key: 'approvalStatus',
                sort: true,
                icon: null,
                prefix: '',
                suffix: '',
                format: MedStatusBadge,
                className: 'faci-col',
            },
            {
                name: null,
                key: 'requestedActions',
                sort: false,
                icon: null,
                prefix: '',
                suffix: '',
                format: ChatColumn,
            },
            {
                name: 'Alerted On',
                key: 'alertCreatedOn',
                sort: true,
                icon: calendarMonth,
                prefix: '',
                suffix: '',
                format: getDate,
                className: 'faci-col',
            },
        ],
    },
    {
        name: 'unavailable medication alert',
        stateName: 'unavailableMedicationAlertsList',
        category: 'unavailableMedicationAlerts',
        path: 'specialtymedornotavailable',
        requestActionPermissionWidgetName: 'unavailableMedicationAlerts',
        defaultSortField: 'alertCreatedOn',
        sidebarName: 'Unavailable Medication',
        atom: unavailableMedicationAlertsAtom,
        atomShownAlerts: unavailableMedicationAlertsAtomShownAlerts,
        atomFilteredAlerts: unavailableMedicationAlertsAtomFiltered,
        sortOrder: unavailableMedicationAlertsAtomUserSettings.sortOrder,
        active: true,
        hidden: unavailableMedicationAlertsAtomUserSettings.hidden,
        position: 0,
        archived: {
            atom: archivedUnavailableMedicationAlertsAtom,
            atomFilteredAlerts: archivedUnavailableMedicationAlertsAtomFiltered,
        },
        refreshSchedule: {
            mins: 0,
            scnds: 0,
        },
        isV2Alert: true,
        usesCursorPaging: false,
        columns: [
            {
                name: 'Patient',
                key: 'patName',
                sort: true,
                icon: accountIcon,
                prefix: '',
                suffix: '',
                format: patientNameCol,
                className: 'faci-col',
            },
            {
                name: 'Facility',
                key: 'facID',
                sort: true,
                icon: homeCity,
                prefix: '',
                suffix: '',
                format: faciNameCol,
                className: 'faci-col',
            },
            {
                name: 'Station',
                key: 'nsID',
                sort: true,
                icon: doctor,
                prefix: '',
                suffix: '',
                format: '',
                className: 'faci-col',
            },
            {
                name: 'Drug Name',
                key: 'drugLabelName',
                sort: true,
                icon: medicineBottle,
                prefix: '',
                suffix: '',
                format: '',
                className: 'faci-col',
            },
            {
                name: 'Med Status',
                key: 'availabilityStatus',
                sort: true,
                icon: null,
                prefix: '',
                suffix: '',
                format: UnavailableMedStatusBadge,
                className: 'faci-col',
            },
            {
                name: 'Alerted On',
                key: 'alertCreatedOn',
                sort: true,
                icon: calendarMonth,
                prefix: '',
                suffix: '',
                format: getDate,
                className: 'faci-col',
            },
        ],
    },

];